import { useState } from 'react';
import { Badge, Card, Empty, ErrorBox, Loading, errorText, useAsync, useToast, type Tone } from '../../shared/ui';
import { fmtDateTime } from '../../shared/format';
import { api, type OutboxMessage, type SyncReport } from '../api';

const KINDS: Record<string, string> = {
  'order.new': 'Pedido nuevo',
  'order.reminder': 'Recordatorio',
  'order.modified': 'Pedido modificado',
  'order.rejected': 'Pedido rechazado',
  'date.review': 'Fecha a validar',
  'price.review': 'Precio a validar',
  'fecha.result': 'Resultado de fecha',
  'precio.result': 'Resultado de precio',
  'weekly.digest': 'Cartera semanal',
  test: 'Prueba',
};

const STATUS: Record<string, { label: string; tone: Tone }> = {
  sent: { label: 'Enviado', tone: 'ok' },
  pending: { label: 'En cola', tone: 'warn' },
  failed: { label: 'Fallido', tone: 'bad' },
  skipped: { label: 'Descartado', tone: 'neutral' },
};

export function Avisos(): JSX.Element {
  const toast = useToast();
  const outbox = useAsync(() => api.get<{ messages: OutboxMessage[] }>('/portal/outbox'), []);
  const sync = useAsync(() => api.get<{ report: SyncReport | null }>('/portal/sync'), []);
  const [running, setRunning] = useState(false);

  async function runNow(): Promise<void> {
    setRunning(true);
    try {
      const { report } = await api.post<{ report: SyncReport }>('/portal/sync');
      toast(report.ok ? 'Repaso de SAP hecho' : `El repaso ha fallado: ${report.error ?? ''}`, report.ok ? 'ok' : 'bad');
      sync.setData({ report });
      void outbox.reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setRunning(false);
    }
  }

  const report = sync.data?.report ?? null;
  return (
    <div className="stack lg">
      <div className="page-head">
        <div>
          <h1>Avisos</h1>
          <p className="muted">
            Correos que envía el portal y último repaso de SAP. El repaso se hace solo cada pocos minutos: busca pedidos nuevos o modificados y envía
            los avisos.
          </p>
        </div>
        <div className="row">
          <button className="btn" onClick={() => void outbox.reload()} disabled={outbox.loading}>
            Recargar
          </button>
          <button className="btn primary" onClick={() => void runNow()} disabled={running}>
            {running ? 'Repasando SAP…' : 'Repasar SAP ahora'}
          </button>
        </div>
      </div>

      <Card title="Último repaso de SAP">
        {sync.error ? (
          <ErrorBox error={sync.error} onRetry={() => void sync.reload()} />
        ) : !sync.data ? (
          <Loading />
        ) : !report ? (
          <p className="muted">Todavía no se ha hecho ningún repaso. Pulsa «Repasar SAP ahora».</p>
        ) : (
          <div className="stack">
            <div className="row">
              {report.ok ? <Badge tone="ok">Correcto</Badge> : <Badge tone="bad">Con error</Badge>}
              <span className="muted">{fmtDateTime(report.at)}</span>
              {report.silent ? <Badge tone="info">Primera carga, sin avisos</Badge> : null}
            </div>
            {report.error ? <div className="callout bad">{report.error}</div> : null}
            <dl className="kv">
              <dt>Pedidos abiertos</dt>
              <dd>{report.openOrders}</dd>
              <dt>Publicados</dt>
              <dd>{report.published}</dd>
              <dt>Modificados</dt>
              <dd>{report.modified}</dd>
              <dt>Recordatorios</dt>
              <dd>{report.reminders}</dd>
              <dt>Cerrados</dt>
              <dd>{report.closed}</dd>
              <dt>Carteras semanales</dt>
              <dd>{report.digests}</dd>
              <dt>Correos</dt>
              <dd>
                {report.mail.sent} enviados{report.mail.failed ? ` · ${report.mail.failed} fallidos` : ''}
              </dd>
            </dl>
          </div>
        )}
      </Card>

      <Card title="Correos (últimos 100)" bodyless>
        {outbox.error ? (
          <div className="card-body">
            <ErrorBox error={outbox.error} onRetry={() => void outbox.reload()} />
          </div>
        ) : !outbox.data ? (
          <Loading />
        ) : outbox.data.messages.length === 0 ? (
          <Empty icon="✉">
            Todavía no se ha enviado ningún aviso. Comprueba en Configuración que el servidor de correo está puesto y que los avisos están activados.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Para</th>
                  <th>Asunto</th>
                  <th>Estado</th>
                  <th className="num">Intentos</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {outbox.data.messages.map((m) => {
                  const st = STATUS[m.status] ?? { label: m.status, tone: 'neutral' as Tone };
                  return (
                    <tr key={m.id}>
                      <td className="nowrap">{fmtDateTime(m.sentAt ?? m.createdAt)}</td>
                      <td className="nowrap">{KINDS[m.kind] ?? m.kind}</td>
                      <td className="pt-wrap">{(m.to ?? []).join(', ')}</td>
                      <td>{m.subject}</td>
                      <td>
                        <Badge tone={st.tone}>{st.label}</Badge>
                      </td>
                      <td className="num">{m.attempts}</td>
                      <td className="pt-wrap subtle">{m.lastError ?? ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
